/**
 * @fileoverview Status filter chips using SegmentedControl
 */

import { memo, useMemo } from 'react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { SegmentedControl } from '@mantine/core'
import type { MantineTheme } from '@mantine/core'
import { Group, Text } from 'components'
import { STATUS_OPTIONS } from '../types'
import type { DisplayStatus } from '../types'

interface StatusChipsProps {
  value: DisplayStatus
  onChange: (value: DisplayStatus) => void
  excludeStatuses?: DisplayStatus[]
}

const getStyles = (theme: MantineTheme) => ({
  root: {
    backgroundColor: theme.colorScheme === 'dark'
      ? theme.colors.dark[5]
      : theme.colors.gray[3],
  },
  control: {
    border: 'none !important',
  },
  indicator: {
    backgroundColor: theme.colors.teal[6],
  },
  label: {
    color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[7],
    '&[data-active]': {
      color: theme.white,
    },
  },
})

/**
 * Segmented control for filtering projects by status.
 * Options: All, Draft, Assigned, Completed
 * Can exclude certain statuses based on user profile (e.g., hide draft for students)
 */
export const StatusChips: FC<StatusChipsProps> = memo(({ value, onChange, excludeStatuses = [] }) => {
  const { t } = useTranslation()

  const data = useMemo(
    () => STATUS_OPTIONS
      .filter(option => !excludeStatuses.includes(option.value))
      .map(option => ({
        value: option.value,
        label: t(option.label),
      })),
    [t, excludeStatuses],
  )

  const handleChange = (newValue: string) => {
    onChange(newValue as DisplayStatus)
  }

  return (
    <Group spacing="sm" noWrap>
      <Text size="sm" weight={600} color="dimmed">
        {t('arc_projects.status_label')}
      </Text>
      <SegmentedControl
        value={value}
        onChange={handleChange}
        data={data}
        size="sm"
        radius="xl"
        aria-label={t('arc_projects.status_filter_aria')}
        styles={getStyles}
      />
    </Group>
  )
})

StatusChips.displayName = 'StatusChips'
